import { Plus, Loader2 } from "lucide-react";

// =====================================================================
// PlanTabsRow — top strip showing all plans for a patient as switchable
// pills + a "+ New Plan" button. Sort: most recently updated first.
// =====================================================================

// Display labels keyed off the derived pipeline_state when present
// (Tx Plan Center maps Accepted + ≥1 done visit → "In Progress",
// stored Completed → "Completed"). Falls back to the raw status enum
// for plans without visit data (Draft / Proposed / Rejected / etc).
const STATUS_BADGES = {
  Draft:                "bg-slate-100 text-slate-700",
  Proposed:             "bg-violet-50 text-violet-700",
  "Partially Accepted": "bg-amber-50 text-amber-700",
  Accepted:             "bg-emerald-50 text-emerald-700",
  "In Progress":        "bg-amber-50 text-amber-800",
  Completed:            "bg-teal-50 text-teal-700",
  Rejected:             "bg-rose-50 text-rose-700",
  Superseded:           "bg-slate-100 text-slate-500",
};

// Map list_treatment_plans -> { display label, badge style key }.
// Mirrors derive_pipeline_state in supabase/migrations/20260616000001
// so this tab strip never disagrees with the Tx Plan Center pipeline.
function deriveBadge(plan) {
  if (plan?.pipeline_state === "in_progress") return "In Progress";
  if (plan?.pipeline_state === "completed")   return "Completed";
  return plan?.status || "Draft";
}

// Plan code falls back to a 4-char prefix of the UUID for plans that
// existed before migration 20260616000006 backfilled plan_code on
// every row. Once that migration is applied in production, every plan
// has a TP-XXXXXX code so this branch is dead.
function planLabel(plan) {
  if (plan?.plan_code) return plan.plan_code;
  if (!plan?.id) return "Plan";
  return "Plan #" + plan.id.slice(0, 4).toUpperCase();
}

export default function PlanTabsRow({
  plans = [],
  activePlanId,
  busyNew = false,
  canCreate = true,
  onPick,                   // (plan_id) => void
  onCreate,                 // () => void
}) {
  // Plans are code-named with random TP-XXXX, which reads as unordered.
  // Number them chronologically (oldest = Plan 1) so it's obvious which
  // is the latest, keeping the TP code as a small sub-label.
  const numberById = (() => {
    const sorted = [...plans].sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
    const map = {};
    sorted.forEach((p, i) => { map[p.id] = i + 1; });
    return map;
  })();

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="plan-tabs-row">
      {plans.length === 0 ? (
        <span className="text-xs text-slate-500">No plans yet.</span>
      ) : null}

      {plans.map((p) => {
        const active = p.id === activePlanId;
        const badgeLabel = deriveBadge(p);
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick?.(p.id)}
            data-testid={`plan-tab-${p.id}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 ${
              active
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"
            }`}
          >
            <span className="flex flex-col items-start leading-tight">
              <span>Plan {numberById[p.id] ?? "?"}</span>
              <span className={`text-[9px] font-normal tracking-normal normal-case ${active ? "text-white/60" : "text-slate-400"}`}>{planLabel(p)}</span>
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full ${STATUS_BADGES[badgeLabel] || STATUS_BADGES.Draft} ${active ? "ring-1 ring-white/30" : ""}`}>
              {badgeLabel}
            </span>
          </button>
        );
      })}

      {canCreate ? (
        <button
          type="button"
          onClick={onCreate}
          disabled={busyNew}
          data-testid="plan-tab-new"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-teal-400 hover:text-teal-600 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 disabled:opacity-40"
        >
          {busyNew ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          New Plan
        </button>
      ) : null}
    </div>
  );
}
